import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
  makeStyles,
  Typography,
  useTheme
} from '@material-ui/core'
import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { CardActionProps } from '../cardActions/cardActions'
import { CardBottom } from '../cardBottom/cardBottom'
import { styles } from './simpleDialog.styles'

interface SimpleDialogProps extends DialogProps {
  open: boolean
  handleClose: () => void
  footerActions?: CardActionProps[]
  hideFooter?: boolean
  title?: string
  children: ReactNode
  dialogContentClassName?: string
  formStyle?:
    | 'default'
    | 'formContent'
    | 'noPadding'
    | 'custom'
    | 'confirmationDialog'
    | 'formDialog'
}

const SimpleDialog: React.FC<SimpleDialogProps> = ({
  open,
  handleClose,
  title,
  children,
  footerActions,
  hideFooter,
  dialogContentClassName,
  formStyle = 'default',
  ...props
}) => {
  const [t] = useTranslation(['simpleDialog', 'globalerisLibrary'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const hasFooterActions = footerActions && footerActions.length > 0

  // Selección de clase según formStyle
  let contentClass: string | undefined = undefined
  let dialogActionClass: string | undefined = undefined
  let dialogClass: string | undefined = undefined
  let childrenClass: string | undefined = undefined
  let titleClass: string | undefined = undefined

  if (formStyle === 'formContent') {
    contentClass = classes.formDialogContent
    dialogActionClass = classes.formDialogActions
  }
  if (formStyle === 'formDialog') {
    contentClass = classes.formDialogContentCustom
    dialogActionClass = classes.formDialogActions
  }
  if (formStyle === 'noPadding') contentClass = classes.noPaddingDialogContent
  if (formStyle === 'custom') contentClass = `${dialogContentClassName || ''}`
  if (formStyle === 'confirmationDialog') {
    dialogActionClass = classes.confirmationDialogActions
    dialogClass = classes.confirmationDialogRoot
    childrenClass = classes.confirmationDialogContentText
    titleClass = title
      ? classes.confirmationDialogWithTitle
      : classes.confirmationDialogWithoutTitle
  }

  const isConfirmationDialog = formStyle === 'confirmationDialog'

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      disableBackdropClick
      disableEscapeKeyDown
      open={open}
      onClose={handleClose}
      {...props}
      className={dialogClass}
    >
      {/* Título */}
      {isConfirmationDialog && title ? (
        <DialogTitle className={titleClass} disableTypography>
          <Typography variant="h5" component="h5">
            {title}
          </Typography>
        </DialogTitle>
      ) : null}

      {/* Contenido */}
      {isConfirmationDialog ? (
        <DialogContent>
          <DialogContentText className={childrenClass}>{children}</DialogContentText>
        </DialogContent>
      ) : (
        <DialogContent className={contentClass}>
          {title && (
            <Typography variant="h5" color="secondary">
              {title}
            </Typography>
          )}
          {children}
        </DialogContent>
      )}

      {/* Acciones */}
      {isConfirmationDialog ? (
        <DialogActions className={dialogActionClass}>
          {footerActions?.map((action, index) => (
            <Button
              key={index}
              onClick={action.handleActionClick}
              color={action.color}
              variant={action.variant}
              disabled={action.disabled}
            >
              {action.label}
            </Button>
          ))}
        </DialogActions>
      ) : hideFooter ? null : hasFooterActions ? (
        <DialogActions className={dialogActionClass}>
          <CardBottom actions={footerActions} />
        </DialogActions>
      ) : (
        <DialogActions>
          <Button size="small" onClick={handleClose}>
            {t('simpleDialog:button.cancel')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default SimpleDialog
